import { Bot, User, AlertTriangle } from 'lucide-react';
import { cn } from '../../ui';
import { MarkdownView } from '../Knowledge/MarkdownView';
import { formatMessageTime } from '../../features/chat/chatModel';
import type { ChatMessage } from '../../features/chat/types';

/**
 * A single chat turn. User turns are right-aligned accent bubbles with plain
 * (whitespace-preserving) text; assistant turns are left-aligned surface
 * bubbles rendered as markdown (headings, lists, fenced code). Assistant error
 * turns get a distinct danger treatment.
 */
export function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  const isError = message.role === 'assistant' && message.error;

  return (
    <div
      className={cn(
        'flex w-full gap-3 animate-fade-in',
        isUser ? 'justify-end' : 'justify-start',
      )}
    >
      {!isUser && (
        <div
          className={cn(
            'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border',
            isError
              ? 'border-red-500/30 bg-red-500/10 text-red-300'
              : 'border-surface-border bg-surface-2 text-accent',
          )}
          aria-hidden="true"
        >
          {isError ? (
            <AlertTriangle className="h-4 w-4" />
          ) : (
            <Bot className="h-4 w-4" />
          )}
        </div>
      )}

      <div
        className={cn(
          'max-w-[80%] rounded-2xl border px-4 py-2.5 text-sm shadow-card',
          isUser
            ? 'border-accent/40 bg-accent-soft/50 text-zinc-100'
            : isError
              ? 'border-red-500/30 bg-red-500/5 text-red-100'
              : 'border-surface-border bg-surface-2 text-zinc-200',
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
        ) : (
          <MarkdownView markdown={message.content} className="text-sm" />
        )}
        <div
          className={cn(
            'mt-1 text-[10px] uppercase tracking-wide',
            isUser ? 'text-accent-fg/70' : 'text-zinc-500',
          )}
        >
          {formatMessageTime(message.createdAt)}
        </div>
      </div>

      {isUser && (
        <div
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-surface-border bg-surface-3 text-zinc-300"
          aria-hidden="true"
        >
          <User className="h-4 w-4" />
        </div>
      )}
    </div>
  );
}

export default MessageBubble;
